/**
 * Bamford Game Engine
 *
 * State machine:
 *   title → commentary → aiming → rolling →
 *     scored  → clearance_brief → clearance_aim → clearance_rolling → (next hoop or more clearances)
 *     missed  → clearance_brief (x1–3) → clearance_aim → clearance_rolling → (retry hoop)
 *   Miss any clearance → Nasr wins → lost
 */

import { Ball } from '../physics/ball.js'
import type { Vec2 } from '../physics/ball.js'
import { Hoop } from '../physics/hoop.js'
import { stepPhysics } from '../physics/step.js'
import { applyFriction } from '../physics/surface.js'
import { resolveBallBallCollision } from '../physics/collision.js'
import { COURT_WIDTH, COURT_HEIGHT, BAMFORD_HOOPS } from '../court/layout.js'
import { BOUNDARY_RESTITUTION } from '../physics/constants.js'
import { GameScene } from '../view/scene.js'
import { BallMesh, BALL_COLORS } from '../view/ballmesh.js'
import { HoopMesh } from '../view/hoopmesh.js'
import { HUD } from '../view/hud.js'
import { Screens } from '../view/screens.js'
import { AimController } from '../input/aim.js'
import { playCheer, playBounce, playSad, playHit } from '../audio.js'

type GameState =
  | 'title' | 'commentary' | 'aiming' | 'rolling'
  | 'scored' | 'missed'
  | 'clearance_brief' | 'clearance_aim' | 'clearance_rolling'
  | 'won' | 'lost'

const PHYSICS_DT = 1 / 240
const RESULT_PAUSE_MS = 1600
const CLEAR_FAIL_PAUSE_MS = 1200
/** Nasr's ball must move this far from start for a clearance to count */
const CLEAR_SUCCESS_DIST = 3.5

const COURT_BOUNDS = {
  minX: 0.3, maxX: COURT_WIDTH  - 0.3,
  minY: 0.3, maxY: COURT_HEIGHT - 0.3,
}

export class BamfordEngine {
  private _scene: GameScene
  private _ball: Ball
  private _ballMesh: BallMesh
  private _nasrBall: Ball
  private _nasrBallMesh: BallMesh
  private _hoop: Hoop
  private _hoopMesh: HoopMesh
  private _hud: HUD
  private _screens: Screens
  private _aim: AimController

  private _state: GameState = 'title'
  private _hoopIndex = 0
  private _bamfordScore = 2
  private _nasrScore = 6
  private _resultTimer = 0
  private _bounced = false
  private _lastTime = 0
  private _physicsAccum = 0

  // Clearance state
  private _clearancesNeeded = 0
  private _clearancesDone = 0
  private _clearanceAfterMiss = false
  private _nasrBallStart: Vec2 = { x: 0, y: 0 }
  private _clearFailed = false   // prevents re-triggering lost while timeout runs

  constructor(canvas: HTMLCanvasElement, hudEl: HTMLElement) {
    this._scene = new GameScene(canvas)
    this._ball = new Ball(0, 0)
    this._ballMesh = new BallMesh(BALL_COLORS.bamford, this._scene.scene)
    this._nasrBall = new Ball(0, 0)
    this._nasrBallMesh = new BallMesh(BALL_COLORS.nasr, this._scene.scene)
    this._nasrBallMesh.setVisible(false)
    this._hoop = new Hoop(0, 0)
    this._hoopMesh = new HoopMesh(this._scene.scene)
    this._hud = new HUD(hudEl)
    this._screens = new Screens()
    this._aim = new AimController(
      this._scene.scene,
      this._scene.camera,
      canvas,
      (shot) => this._onShot(shot.angle, shot.speed)
    )
    this._screens.showTitle(() => this._startGame())
  }

  // ── Setup ──────────────────────────────────────────────────────────────────

  private _startGame(): void {
    this._hoopIndex = 0
    this._bamfordScore = 2
    this._nasrScore = 6
    this._setupHoop()
    this._setState('commentary')
  }

  private _setupHoop(): void {
    const config = BAMFORD_HOOPS[this._hoopIndex]
    this._ball.pos.x = config.ballStart.x
    this._ball.pos.y = config.ballStart.y
    this._ball.stop()
    this._nasrBallMesh.setVisible(false)
    this._hoop = new Hoop(config.hoopPos.x, config.hoopPos.y, config.hoopFacing)
    this._hoopMesh.update(this._hoop)
    this._bounced = false
    this._scene.setEvening(this._hoopIndex >= 2)
  }

  private _setupClearance(): void {
    const hoop = BAMFORD_HOOPS[this._hoopIndex].hoopPos

    // Nasr's ball: in the hoop area but further out — hoop still in play
    const lateral = (Math.random() - 0.5) * 1.5
    const nasrX = Math.max(1, Math.min(COURT_WIDTH - 1, hoop.x + lateral))
    const nasrY = Math.max(1, Math.min(COURT_HEIGHT - 1, hoop.y + 1.8 + Math.random() * 0.8))
    this._nasrBall.pos = { x: nasrX, y: nasrY }
    this._nasrBall.stop()
    this._nasrBallStart = { x: nasrX, y: nasrY }
    this._nasrBallMesh.setVisible(true)

    // Bamford's ball: to one side, a few units away
    const side = Math.random() < 0.5 ? -1 : 1
    this._ball.pos = {
      x: Math.max(1.5, Math.min(COURT_WIDTH  - 1.5, nasrX + side * 4.5)),
      y: Math.max(1.5, Math.min(COURT_HEIGHT - 1.5, nasrY + 1.5 + Math.random())),
    }
    this._ball.stop()
    this._clearFailed = false
  }

  // ── State machine ──────────────────────────────────────────────────────────

  private _setState(state: GameState): void {
    this._state = state
    this._hud.clear()

    if (state === 'commentary') {
      const config = BAMFORD_HOOPS[this._hoopIndex]
      this._hud.showScoreboard(this._nasrScore, this._bamfordScore, config.label)
      this._hud.showCommentary(config.commentary, () => this._setState('aiming'))
      this._aim.disable()
    }

    if (state === 'aiming') {
      this._hud.showScoreboard(this._nasrScore, this._bamfordScore, BAMFORD_HOOPS[this._hoopIndex].label)
      this._hud.showAimInstruction()
      this._aim.enable()
    }

    if (state === 'rolling') {
      this._hud.showScoreboard(this._nasrScore, this._bamfordScore, BAMFORD_HOOPS[this._hoopIndex].label)
      this._aim.disable()
    }

    if (state === 'scored') {
      this._bamfordScore++
      this._hud.showScoreboard(this._nasrScore, this._bamfordScore, BAMFORD_HOOPS[this._hoopIndex].label)
      this._hud.showResult(`✔ HOOP!  ${this._bamfordScore}–${this._nasrScore}`, '#44ff44')
      playCheer()
      this._resultTimer = 0
    }

    if (state === 'missed') {
      this._hud.showScoreboard(this._nasrScore, this._bamfordScore, BAMFORD_HOOPS[this._hoopIndex].label)
      this._hud.showResult(this._bounced ? 'BOUNCED OFF...' : 'MISSED...', '#ff4444')
      playSad()
      this._resultTimer = 0
    }

    if (state === 'clearance_brief') {
      this._hud.showScoreboard(this._nasrScore, this._bamfordScore, 'CLEARANCE')
      this._hud.showClearanceCommentary(this._getClearanceLines(), () => this._setState('clearance_aim'))
      this._aim.disable()
    }

    if (state === 'clearance_aim') {
      this._hud.showScoreboard(this._nasrScore, this._bamfordScore, 'CLEARANCE')
      this._hud.showAimInstruction()
      this._aim.enable()
    }

    if (state === 'clearance_rolling') {
      this._hud.showScoreboard(this._nasrScore, this._bamfordScore, 'CLEARANCE')
      this._aim.disable()
    }

    if (state === 'won') {
      this._nasrBallMesh.setVisible(false)
      this._screens.showWin(() => {
        this._screens.hide()
        this._screens.showTitle(() => this._startGame())
      })
    }

    if (state === 'lost') {
      this._nasrBallMesh.setVisible(false)
      this._screens.showLose(this._hoopIndex, () => {
        this._screens.hide()
        this._screens.showTitle(() => this._startGame())
      })
    }
  }

  private _getClearanceLines(): string[] {
    const hoopLabel = BAMFORD_HOOPS[this._hoopIndex].label

    if (!this._clearanceAfterMiss) {
      return [
        `Nasr is sitting at ${hoopLabel}!`,
        'Clear him away before you proceed.',
      ]
    }

    const lines = [
      ['You missed. Clear Nasr\'s ball first.'],
      ['Nasr is lurking. Get him away from the hoop.'],
      ['Not good enough. Clear his ball.'],
      ['Bamford needs to clear before he can try again.'],
      ['Nasr has position. Knock him away.'],
    ]
    return lines[(this._clearancesDone) % lines.length]
  }

  // ── Shot handler ───────────────────────────────────────────────────────────

  private _onShot(angle: number, speed: number): void {
    if (this._state === 'aiming') {
      this._ball.shoot(angle, speed)
      this._bounced = false
      playHit()
      this._setState('rolling')
    } else if (this._state === 'clearance_aim') {
      this._ball.shoot(angle, speed)
      playHit()
      this._setState('clearance_rolling')
    }
  }

  // ── Main loop ──────────────────────────────────────────────────────────────

  tick(timestamp: number): void {
    if (this._lastTime === 0) this._lastTime = timestamp
    const elapsed = Math.min((timestamp - this._lastTime) / 1000, 0.05)
    this._lastTime = timestamp
    this._update(elapsed)
    this._render()
  }

  private _update(elapsed: number): void {

    // ── Hoop shot physics (ball keeps rolling through scored state) ──────────
    if (this._state === 'rolling' || this._state === 'scored') {
      this._physicsAccum += elapsed

      while (this._physicsAccum >= PHYSICS_DT) {
        const prev = { ...this._ball.pos }
        const result = stepPhysics(this._ball, prev, [this._hoop], COURT_BOUNDS, PHYSICS_DT)

        if (result.hoopHit !== null && !this._bounced) {
          this._bounced = true
          playBounce()
        }
        if (result.scored && this._state === 'rolling') {
          this._setState('scored')
        }
        if (result.stopped && this._state === 'rolling') {
          this._physicsAccum = 0
          this._setState('missed')
          return
        }

        this._physicsAccum -= PHYSICS_DT
      }
    }

    // ── Result pause → clearance ─────────────────────────────────────────────
    if (this._state === 'scored' || this._state === 'missed') {
      this._resultTimer += elapsed * 1000
      if (this._resultTimer > RESULT_PAUSE_MS) {
        if (this._state === 'scored') {
          if (this._bamfordScore >= 7) {
            this._setState('won')
          } else {
            this._clearanceAfterMiss = false
            this._clearancesNeeded   = 1
            this._clearancesDone     = 0
            this._setupClearance()
            this._setState('clearance_brief')
          }
        } else {
          this._clearanceAfterMiss = true
          this._clearancesNeeded   = 1 + Math.floor(Math.random() * 3)  // 1–3
          this._clearancesDone     = 0
          this._setupClearance()
          this._setState('clearance_brief')
        }
      }
    }

    // ── Clearance physics (hoop stays live) ─────────────────────────────────
    if (this._state === 'clearance_rolling') {
      this._physicsAccum += elapsed

      while (this._physicsAccum >= PHYSICS_DT) {
        const dt = PHYSICS_DT
        const prev = { ...this._ball.pos }

        // Bamford's ball: full hoop physics — hoop is live during clearance
        const result = stepPhysics(this._ball, prev, [this._hoop], COURT_BOUNDS, dt)

        // Nasr's ball: friction + advance + boundary
        applyFriction(this._nasrBall, dt)
        this._nasrBall.pos.x += this._nasrBall.vel.x * dt
        this._nasrBall.pos.y += this._nasrBall.vel.y * dt
        this._bounceAtBoundary(this._nasrBall)

        // Ball-ball collision
        resolveBallBallCollision(this._ball, this._nasrBall)

        if (result.hoopHit !== null && !this._bounced) {
          this._bounced = true
          playBounce()
        }

        // IN-OFF — scored the hoop during clearance, skip remaining clearances
        if (result.scored && !this._clearFailed) {
          this._physicsAccum = 0
          this._clearFailed = true
          this._bamfordScore++
          this._hud.clear()
          playCheer()
          this._hud.showInOff(this._bamfordScore, this._nasrScore, () => {
            if (this._bamfordScore >= 7) {
              this._setState('won')
            } else {
              this._hoopIndex++
              this._setupHoop()
              this._setState('commentary')
            }
          })
          return
        }

        // Both stopped — evaluate clearance result
        if (this._ball.state === 'stopped' && this._nasrBall.state === 'stopped' && !this._clearFailed) {
          this._physicsAccum = 0
          const dx = this._nasrBall.pos.x - this._nasrBallStart.x
          const dy = this._nasrBall.pos.y - this._nasrBallStart.y
          const distMoved = Math.sqrt(dx * dx + dy * dy)

          if (distMoved >= CLEAR_SUCCESS_DIST) {
            this._clearancesDone++
            if (this._clearancesDone >= this._clearancesNeeded) {
              if (this._clearanceAfterMiss) {
                this._setupHoop()
                this._setState('commentary')
              } else {
                this._hoopIndex++
                this._setupHoop()
                this._setState('commentary')
              }
            } else {
              this._setupClearance()
              this._setState('clearance_brief')
            }
          } else {
            this._clearFailed = true
            this._hud.showResult('NASR SCORES! YOU LOSE.', '#ff2222')
            setTimeout(() => this._setState('lost'), CLEAR_FAIL_PAUSE_MS)
          }
          return
        }

        this._physicsAccum -= PHYSICS_DT
      }
    }

    // ── Aim line ─────────────────────────────────────────────────────────────
    if (this._state === 'aiming' || this._state === 'clearance_aim') {
      this._aim.updateAimLine(this._ball)
    }
  }

  private _bounceAtBoundary(ball: Ball): void {
    if (ball.pos.x - ball.radius < COURT_BOUNDS.minX) {
      ball.pos.x = COURT_BOUNDS.minX + ball.radius
      if (ball.vel.x < 0) ball.vel.x *= -BOUNDARY_RESTITUTION
    }
    if (ball.pos.x + ball.radius > COURT_BOUNDS.maxX) {
      ball.pos.x = COURT_BOUNDS.maxX - ball.radius
      if (ball.vel.x > 0) ball.vel.x *= -BOUNDARY_RESTITUTION
    }
    if (ball.pos.y - ball.radius < COURT_BOUNDS.minY) {
      ball.pos.y = COURT_BOUNDS.minY + ball.radius
      if (ball.vel.y < 0) ball.vel.y *= -BOUNDARY_RESTITUTION
    }
    if (ball.pos.y + ball.radius > COURT_BOUNDS.maxY) {
      ball.pos.y = COURT_BOUNDS.maxY - ball.radius
      if (ball.vel.y > 0) ball.vel.y *= -BOUNDARY_RESTITUTION
    }
  }

  private _render(): void {
    this._ballMesh.update(this._ball)
    this._nasrBallMesh.update(this._nasrBall)
    this._scene.render()
  }
}
